import React from 'react';

/**
 * Правая колонка метрик платформы по артборду: задачи по сервисам списком с
 * полосками (клик фильтрует весь экран) и то, что мы знаем про бота встреч.
 *
 * 🪤 В артборде на месте нижней карточки «Сейчас на платформе» — живые
 * счётчики очереди. Общего агрегата по платформе нет: очередь своя у каждого
 * ASR-сервиса, в его собственном `/admin/queue`
 * (docs/backlog/redesign-shell-gaps.md). Место занято тем, что здесь и было и
 * чего в артборде нет вовсе, — встречами бота: это единственный измеритель
 * спроса на площадки, которых мы не поддерживаем. Под ним — такой же
 * измеритель для ссылок на запись (видеохостинги, облачные диски).
 */

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('ru-RU') : '—');

// Факт исполнения сервисов, чья работа — не задача обработки (bit-metrics
// ACTION_EVENTS). Свои единицы и в «всего» не складываются: сохранение в
// Историю повторяет уже посчитанную расшифровку, скачивание по ссылке —
// задачу bit-transcribe-parakeet (просьба владельца 05.10).
// Бота здесь нет: его встречи — в карточке «Бот на встречах» ниже.
const ACTION_TITLES = [
  ['bit-fetch', 'Скачано по ссылке'],
  ['bit-export', 'Выгружено в Word или PDF'],
  ['bit-history', 'Сохранено в Историю'],
];

// Виды задач bit-llm-summarize: одной строкой они весят по-разному.
const LLM_KIND_TITLES = [['summary', 'протоколы'], ['title', 'названия'], ['chat', 'вопросы'],
  ['other', 'прочее']];

// Ключи — имена адаптеров (bit-meetbot/app/platforms/*.name).
const PLATFORM_TITLES = { telemost: 'Телемост', ktalk: 'Контур.Толк',
  bitlink: 'БИТ.Link', linkgo: 'БИТ.LinkGo', zoom: 'Zoom' };

export default function ServicesPanel({ data, selectedService, onSelectService }) {
  if (!data) return null;

  // Нулевые строки не показываем: ноль здесь значил «у сервиса другая
  // единица работы», а не «сервис простаивал» — та работа в карточке ниже.
  const services = Object.entries(data.tasks_by_service || {})
    .filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);
  const actions = data.actions_30d || {};
  const llmKinds = LLM_KIND_TITLES.filter(([k]) => (data.llm_by_kind_30d || {})[k] > 0);
  const platforms = Object.entries(data.meetings_by_platform || {}).sort((a, b) => b[1] - a[1]);
  const requests = Object.entries(data.meeting_link_requests || {}).sort((a, b) => b[1] - a[1]);
  const videoRequests = Object.entries(data.video_link_requests || {}).sort((a, b) => b[1] - a[1]);
  const top = services.length ? services[0][1] : 1;
  const total = services.reduce((acc, [, n]) => acc + n, 0);

  return (
    <aside className="flex flex-col gap-3">
      <div className="flex flex-col gap-[10px] rounded-card bg-paper-card px-[22px] py-5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[12px] text-paper-ink-3">Задачи по сервисам · 30 дней</span>
          <button type="button"
                  onClick={() => selectedService && onSelectService(selectedService)}
                  className={`text-[12px] ${selectedService ? 'text-paper-accent' : 'text-paper-ink-3'}`}>
            {selectedService ? 'Сбросить' : `${fmt(total)} всего`}
          </button>
        </div>

        {services.map(([name, count]) => {
          const on = selectedService === name;
          const kinds = name === 'bit-llm-summarize' && llmKinds.length > 0;
          return (
            <React.Fragment key={name}>
              <button
                type="button"
                onClick={() => onSelectService(name)}
                className={`-mx-2 flex items-center gap-[10px] rounded-chip px-2 py-[6px] text-left
                            transition-colors ${on ? 'bg-paper-bg' : 'hover:bg-paper-panel'}`}
              >
                <span className={`flex-1 truncate font-mono text-[12px] ${
                  !selectedService || on ? 'text-paper-ink' : 'text-paper-hint'}`}>
                  {name}
                </span>
                <span className="h-[5px] w-[90px] shrink-0 overflow-hidden rounded-[3px] bg-paper-bg">
                  <span className={`block h-full rounded-[3px] ${on ? 'bg-paper-accent' : 'bg-paper-ink'}`}
                        style={{ width: `${Math.round((count / top) * 100)}%` }} />
                </span>
                <span className="w-[64px] shrink-0 text-right font-mono text-[12px] text-paper-ink">
                  {fmt(count)}
                </span>
              </button>
              {kinds && (
                <div className="-mt-[6px] pl-0 text-[11px] text-paper-ink-3">
                  {llmKinds.map(([k, label]) => `${label} ${fmt(data.llm_by_kind_30d[k])}`).join(' · ')}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {ACTION_TITLES.some(([svc]) => actions[svc] !== undefined) && (
        <div className="flex flex-col gap-2 rounded-card bg-paper-card px-[22px] py-5">
          <div className="text-[12px] text-paper-ink-3">Что ещё сделано · 30 дней</div>
          {ACTION_TITLES.map(([svc, label]) => (
            <div key={svc} className="flex items-baseline justify-between gap-2 text-[13px] text-paper-ink-2">
              <span className="min-w-0 truncate">
                {label} <span className="font-mono text-[11px] text-paper-hint">{svc}</span>
              </span>
              <b className="font-mono font-medium text-paper-ink">{fmt(actions[svc] || 0)}</b>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 rounded-card bg-paper-card px-[22px] py-5">
        <div className="text-[12px] text-paper-ink-3">Бот на встречах</div>
        <div className="flex justify-between text-[13px] text-paper-ink-2">
          <span>Сегодня</span>
          <b className="font-mono font-medium text-paper-ink">{fmt(data.meetings_today)}</b>
        </div>
        <div className="flex justify-between text-[13px] text-paper-ink-2">
          <span>За 30 дней</span>
          <b className="font-mono font-medium text-paper-ink">{fmt(data.meetings_30d)}</b>
        </div>

        {platforms.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-[6px]">
            {platforms.map(([key, count]) => (
              <span key={key} className="rounded-chip bg-paper-bg px-[10px] py-1 text-[12px] text-paper-ink">
                {PLATFORM_TITLES[key] || key} · {fmt(count)}
              </span>
            ))}
          </div>
        )}

        {/* Отказ «не понимаю ссылку» — это голос за площадку, которой у нас нет.
            Пустого блока быть не должно: ноль строк и отсутствие спроса читаются
            одинаково, а означают разное. */}
        {requests.length > 0 && (
          <>
            <div className="mt-3 text-[12px] text-paper-ink-3">Просили за 7 дней площадки, которых нет</div>
            <div className="flex flex-wrap gap-[6px]">
              {requests.map(([key, count]) => (
                <span key={key} className="rounded-chip bg-paper-bg px-[10px] py-1 font-mono text-[12px] text-paper-ink">
                  {key} · {fmt(count)}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Ссылки на запись, которые экран распознавания отклонил, — спрос на
          следующую площадку или облачный диск. Число — сколько разных людей
          пробовали хост, а не сколько раз нажали. Пусто — блока нет, по той же
          причине, что и у бота. */}
      {videoRequests.length > 0 && (
        <div className="flex flex-col gap-2 rounded-card bg-paper-card px-[22px] py-5">
          <div className="text-[12px] text-paper-ink-3">Ссылки, которые не приняли · 30 дней</div>
          <div className="text-[12px] text-paper-hint">сколько человек пробовали</div>
          <div className="flex flex-wrap gap-[6px]">
            {videoRequests.map(([host, count]) => (
              <span key={host} className="rounded-chip bg-paper-bg px-[10px] py-1 font-mono text-[12px] text-paper-ink">
                {host} · {fmt(count)}
              </span>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
